import { Direction, PassengerType } from "@/models/Enums";
import { passengerOptions } from "@/modules/Quickform/config/passengerOptions";
import { Resource } from "@/modules/Quickform/models/QuickformApi";
import { PassengerItem } from "@/modules/Quickform/models/QuickformStates";
import { defineStore } from "pinia";

const getInitialState = () => {
  return {
    outward: [] as PassengerItem[],
    return: [] as PassengerItem[],
    differentDetails: false,
  };
};

export const usePassengerStore = defineStore("passenger", {
  state: () => {
    return getInitialState();
  },
  getters: {
    getPassengers:
      (state) =>
      (direction: Direction): PassengerItem[] => {
        switch (direction) {
          case Direction.Outward:
            return state.outward as PassengerItem[];
          case Direction.Return:
            return state.return as PassengerItem[];
        }
      },
    getPassengersPlaceholder:
      (state) =>
      (hasReturn?: boolean): string => {
        if (state.differentDetails && hasReturn) {
          return state.outward.length || state.return.length 
            ? `${state.outward.length} passengers, ${state.return.length} passengers`
            : "Add passengers";
        }
        return state.outward.length
          ? `${state.outward.length} passengers`
          : "Add passengers";
      },
    getDifferentDetails: (state) => {
      return state.differentDetails;
    },
  },
  actions: {
    setPassenger(direction: Direction, passenger: PassengerItem) {
      const passengerArray = this.getPassengers(direction);
      const count = passengerArray.filter(
        (p) => p.passengerType === passenger.passengerType
      ).length;
      if (count > passenger.amount) {
        this.removePassenger(
          direction,
          passenger.passengerType,
          passenger.amount
        );
      } else {
        this.addPassenger(direction, passenger);
      }
    },
    addPassenger(direction: Direction, passenger: PassengerItem) {
      if (passenger.amount === 0) return;

      for (let i = 0; i < passenger.amount; i++) {
        if (direction === Direction.Outward) {
          if (
            !this.outward.find(
              (p) =>
                p.passengerType === passenger.passengerType &&
                p.amount === i + 1
            )
          ) {
            this.outward.push({
              ...passenger,
              amount: i + 1,
            });
          }
        } else {
          if (
            !this.return.find(
              (p) =>
                p.passengerType === passenger.passengerType &&
                p.amount === i + 1
            )
          ) {
            this.return.push({
              ...passenger,
              amount: i + 1,
            });
          }
        }
      }
    },
    removePassenger(
      direction: Direction,
      passengerType: PassengerType,
      value: number
    ) {
      if (direction === Direction.Outward && this.outward.length > 0) {
        this.outward = this.outward.filter(
          (p) => p.amount <= value || p.passengerType !== passengerType
        );
      } else if (this.return.length > 0) {
        this.return = this.return.filter(
          (p) => p.amount <= value || p.passengerType !== passengerType
        );
      }
    },
    setPassengers(direction: Direction, passengers: PassengerItem[]) {
      if (direction === Direction.Outward) {
        this.outward = passengers;
      } else {
        this.return = passengers;
      }
    },
    setDifferentDetails(differentDetails: boolean) {
      this.differentDetails = differentDetails;
    },
    getPassengersForApi(direction: Direction) {
      const passengerArray = this.getPassengers(direction);

      const formattedPassengers: PassengerItem[] = [];

      passengerArray.forEach((obj) => {
        const { passengerType, amount } = obj;
        const existingIndex = formattedPassengers.findIndex(
          (item) => item.passengerType === passengerType
        );

        if (
          existingIndex === -1 ||
          formattedPassengers[existingIndex].amount < amount
        ) {
          if (existingIndex !== -1) {
            formattedPassengers.splice(existingIndex, 1);
          }
          formattedPassengers.push(obj);
        }
      });

      const resources: Resource[] = [];

      formattedPassengers.map((p) => {
        passengerOptions.find((option) => {
          if (option.passengerType === p.passengerType) {
            resources.push({
              amount: p.amount.toString(),
              resourceCode: option.resourceCode,
              type: option.type,
              resourceType: option.resourceType,
            });
          }
        });
      });

      return resources;
    },
  },
  persist: {
    storage: sessionStorage,
  },
});
